import { Login } from "@/components/auth/login";
import { ClientContextBanner } from "@/components/shell/client-context-banner";
import { ContextRouteSync } from "@/components/shell/context-route-sync";
import { Sidebar } from "@/components/shell/sidebar";
import { useAdminUserContext } from "@/hooks/use-admin-user-context";
import {
	AuthenticatedTemplate,
	UnauthenticatedTemplate,
} from "@azure/msal-react";
import type { QueryClient } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useEffect } from "react";
import { Toaster } from "sonner";

/**
 * Root route declares the router context shape so route guards can read
 * `queryClient` from `args.context.queryClient` in their `beforeLoad`. The
 * `queryClient` itself is provided by `main.tsx` when constructing the
 * router.
 */
export const Route = createRootRouteWithContext<{
	queryClient: QueryClient;
}>()({
	component: RootComponent,
});

function RootComponent() {
	// Eagerly load the admin user-context once authenticated. Route guards
	// also call this lazily via `ensureQueryData`, but loading at the root
	// keeps the sync store warmed for non-route consumers (sidebar, etc.).
	useAdminUserContext();

	useEffect(() => {
		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
		const handleChange = () => {
			document.documentElement.classList.toggle(
				"dark",
				mediaQuery.matches,
			);
		};

		// Initial check
		handleChange();

		// Listener
		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, []);

	return (
		<>
			<AuthenticatedTemplate>
				<div className="flex h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans">
					<ContextRouteSync />
					<Sidebar />
					<main className="flex-1 overflow-auto flex flex-col">
						{/* Client mode only: which client, go-live status, Switch /
						    Open client app / Exit. Sticky so it stays in view. */}
						<div className="sticky top-0 z-20">
							<ClientContextBanner />
						</div>
						<div className="flex-1 p-8">
							<Outlet />
						</div>
					</main>
				</div>
				{/* Renders the `toast.*` calls the pages make (sonner needs one
				    mounted Toaster). Follows the same system colour scheme as
				    the `dark` class toggle above. */}
				<Toaster richColors closeButton theme="system" />
				<TanStackRouterDevtools />
			</AuthenticatedTemplate>

			<UnauthenticatedTemplate>
				<Login />
			</UnauthenticatedTemplate>
		</>
	);
}
